import { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { MessageSquare, AlertCircle, FileText } from "lucide-react";
import { useCondos } from "context/CondosContext";
import { useAuth } from "context/AuthContext";
import { Select } from "components/ui/Select";
import { MultiSelect } from "components/ui/MultiSelect";
import { useReportData } from "hooks/useReportData";
import { useSummaryStats } from "hooks/useSummaryStats";
import {
  transformMessagesData,
  transformCasesByTypeData,
  transformCasesByCategoryData,
} from "utils/reportDataTransformers";
import { MOBILE_PAGE_TITLE_MENU_INSET } from "constants/pageLayout";

type ReportType = "messages" | "cases_by_priority" | "cases_by_category";
type PeriodType = "7_days" | "30_days";

type Locale = "es" | "en";

function getReportOptions(locale: Locale) {
  return [
    {
      value: "messages",
      label: locale === "es" ? "Mensajes" : "Messages",
    },
    {
      value: "cases_by_priority",
      label: locale === "es" ? "Casos por prioridad" : "Cases by priority",
    },
    {
      value: "cases_by_category",
      label: locale === "es" ? "Casos por categoría" : "Cases by category",
    },
  ];
}

function getPeriodOptions(locale: Locale) {
  return [
    {
      value: "7_days",
      label: locale === "es" ? "Últimos 7 días" : "Last 7 days",
    },
    {
      value: "30_days",
      label: locale === "es" ? "Últimos 30 días" : "Last 30 days",
    },
  ];
}

const TYPE_SERIES: Array<{
  key: "issue_high" | "issue_medium" | "issue_low" | "non_issue" | "unknown";
  color: string;
  labelEs: string;
  labelEn: string;
}> = [
  {
    key: "issue_high",
    color: "#F5BD1E",
    labelEs: "Alta prioridad",
    labelEn: "High priority",
  },
  {
    key: "issue_medium",
    color: "#FFE134",
    labelEs: "Prioridad media",
    labelEn: "Medium priority",
  },
  {
    key: "issue_low",
    color: "#7BB662",
    labelEs: "Baja prioridad",
    labelEn: "Low priority",
  },
  {
    key: "non_issue",
    color: "#5F9C3F",
    labelEs: "No es un problema",
    labelEn: "Not an issue",
  },
  {
    key: "unknown",
    color: "#D1D5DB",
    labelEs: "Desconocido",
    labelEn: "Unknown",
  },
];

function getCategoryLabel(categoryKey: string, locale: Locale): string {
  const es: Record<string, string> = {
    infrastructureMaintenance: "Infraestructura",
    sanitaryIssues: "Problemas Sanitarios",
    neighborRelations: "Relaciones Vecinales",
    commonExpensesAdmin: "Gastos Comunes",
    movesAndWorks: "Mudanzas y Obras",
    securityAccess: "Seguridad",
    emergencies: "Emergencias",
    pets: "Mascotas",
    amenities: "Amenidades",
    generalInformation: "Información General",
  };

  const en: Record<string, string> = {
    infrastructureMaintenance: "Infrastructure",
    sanitaryIssues: "Sanitary issues",
    neighborRelations: "Neighbor relations & conflicts",
    commonExpensesAdmin: "Common expenses & administration",
    movesAndWorks: "Moves, renovations & unauthorized works",
    securityAccess: "Security & access control",
    emergencies: "Emergencies & critical events",
    pets: "Pets & pet-related issues",
    amenities: "Amenities & reservations",
    generalInformation: "General information & non-issue requests",
  };

  return locale === "es" ? es[categoryKey] || categoryKey : en[categoryKey] || categoryKey;
}

export default function Reports() {
  const { condos } = useCondos();
  const { lang } = useAuth();
  const locale: Locale = lang === "es" ? "es" : "en";
  const [reportType, setReportType] = useState<ReportType>("messages");
  const [selectedCondoIds, setSelectedCondoIds] = useState<number[]>([]);
  const [period, setPeriod] = useState<PeriodType>("7_days");

  // Determine if we should show periods (single condo) or buildings (multiple or none)
  const showPeriods = selectedCondoIds.length === 1;

  const { data, casesData, loading, error } = useReportData(
    reportType,
    period,
    selectedCondoIds
  );

  const summaryStats = useSummaryStats(period, selectedCondoIds);

  const chartData = useMemo(
    () =>
      transformMessagesData(
        data,
        selectedCondoIds,
        period,
        showPeriods,
        locale
      ),
    [data, selectedCondoIds, period, showPeriods, locale]
  );

  const casesByTypeData = useMemo(
    () =>
      transformCasesByTypeData(casesData, period, showPeriods, locale),
    [casesData, period, showPeriods, locale]
  );

  const casesByCategoryData = useMemo(
    () =>
      transformCasesByCategoryData(
        casesData,
        period,
        showPeriods,
        locale
      ),
    [casesData, period, showPeriods, locale]
  );

  const buildingOptions = useMemo(() => {
    // Add "Todos los edificios" option at the beginning
    return [
      {
        value: "all",
        label: locale === "es" ? "Todos los edificios" : "All buildings",
      },
      ...condos.map((condo) => ({
        value: String(condo.id),
        label: condo.name,
      })),
    ];
  }, [condos, locale]);

  const getReportTitle = () => {
    const reportName =
      reportType === "messages"
        ? locale === "es"
          ? "Mensajes"
          : "Messages"
        : reportType === "cases_by_priority"
          ? locale === "es"
            ? "Casos por prioridad"
            : "Cases by priority"
          : locale === "es"
            ? "Casos por categoría"
            : "Cases by category";

    const suffix = showPeriods
      ? locale === "es"
        ? "por periodo"
        : "by period"
      : locale === "es"
        ? "por edificio"
        : "by building";

    return `${reportName} ${suffix}`;
  };

  const handleBuildingChange = (values: string[]) => {
    // Empty array means show all buildings
    setSelectedCondoIds(values.map((v) => parseInt(v)));
  };

  const selectedBuildingValues = selectedCondoIds.map((id) => String(id));

  return (
    <div className="w-full h-full max-w-7xl overflow-y-auto bg-white py-4 max-lg:pl-0 max-lg:pr-4 lg:px-6">
      <h1
        className={`mb-6 text-2xl font-bold text-gray-900 ${MOBILE_PAGE_TITLE_MENU_INSET}`}
      >
        {locale === "es" ? "Reportes" : "Reports"}
      </h1>

      <div className="max-lg:pl-4 lg:pl-0 space-y-6 lg:space-y-8">
        <FiltersSection
          locale={locale}
          reportType={reportType}
          onReportTypeChange={setReportType}
          selectedBuildingValues={selectedBuildingValues}
          onBuildingChange={handleBuildingChange}
          buildingOptions={buildingOptions}
          period={period}
          onPeriodChange={setPeriod}
        />

        <SummaryCards stats={summaryStats} locale={locale} />

        <ReportCard
          locale={locale}
          reportType={reportType}
          loading={loading}
          error={error}
          title={getReportTitle()}
          chartData={chartData}
          casesByTypeData={casesByTypeData}
          casesByCategoryData={casesByCategoryData}
        />
      </div>
    </div>
  );
}

interface FiltersSectionProps {
  locale: Locale;
  reportType: ReportType;
  onReportTypeChange: (value: ReportType) => void;
  selectedBuildingValues: string[];
  onBuildingChange: (values: string[]) => void;
  buildingOptions: Array<{ value: string; label: string }>;
  period: PeriodType;
  onPeriodChange: (value: PeriodType) => void;
}

function FiltersSection({
  locale,
  reportType,
  onReportTypeChange,
  selectedBuildingValues,
  onBuildingChange,
  buildingOptions,
  period,
  onPeriodChange,
}: FiltersSectionProps) {
  const reportLabel = locale === "es" ? "Reporte" : "Report";
  const buildingLabel = locale === "es" ? "Edificio" : "Building";
  const periodLabel = locale === "es" ? "Periodo" : "Period";

  const reportPlaceholder =
    locale === "es" ? "Seleccionar reporte" : "Select a report";
  const buildingsPlaceholder =
    locale === "es" ? "Seleccionar edificios" : "Select buildings";
  const periodPlaceholder =
    locale === "es" ? "Seleccionar periodo" : "Select a period";

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {reportLabel}
          </label>
          <Select
            value={reportType}
            onChange={(value) => onReportTypeChange(value as ReportType)}
            options={getReportOptions(locale)}
            placeholder={reportPlaceholder}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {buildingLabel}
          </label>
          <MultiSelect
            value={selectedBuildingValues}
            onChange={onBuildingChange}
            options={buildingOptions}
            placeholder={buildingsPlaceholder}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {periodLabel}
          </label>
          <Select
            value={period}
            onChange={(value) => onPeriodChange(value as PeriodType)}
            options={getPeriodOptions(locale)}
            placeholder={periodPlaceholder}
          />
        </div>
      </div>
    </div>
  );
}

interface SummaryCardsProps {
  locale: Locale;
  stats: {
    mensajes: number;
    casos: number;
    incidentes: number;
  };
}

function SummaryCards({ stats, locale }: SummaryCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <SummaryCard
        icon={
          <MessageSquare className="w-12 h-12 text-blue-600 flex-shrink-0" />
        }
        value={stats.mensajes}
        label={locale === "es" ? "mensajes respondidos" : "responded messages"}
        bgColor="from-blue-50 to-blue-100"
        borderColor="border-blue-200"
        textColor="text-blue-900"
        labelColor="text-blue-700"
      />
      <SummaryCard
        icon={<FileText className="w-12 h-12 text-purple-600 flex-shrink-0" />}
        value={stats.casos}
        label={locale === "es" ? "casos gestionados" : "managed cases"}
        bgColor="from-purple-50 to-purple-100"
        borderColor="border-purple-200"
        textColor="text-purple-900"
        labelColor="text-purple-700"
      />
      <SummaryCard
        icon={
          <AlertCircle className="w-12 h-12 text-orange-600 flex-shrink-0" />
        }
        value={stats.incidentes}
        label={
          locale === "es" ? "incidentes reportados" : "reported incidents"
        }
        bgColor="from-orange-50 to-orange-100"
        borderColor="border-orange-200"
        textColor="text-orange-900"
        labelColor="text-orange-700"
      />
    </div>
  );
}

interface SummaryCardProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  labelColor: string;
}

function SummaryCard({
  icon,
  value,
  label,
  bgColor,
  borderColor,
  textColor,
  labelColor,
}: SummaryCardProps) {
  return (
    <div
      className={`bg-gradient-to-br ${bgColor} p-6 rounded-lg border ${borderColor} shadow-sm`}
    >
      <div className="flex items-center gap-4 mb-3">
        {icon}
        <p className={`text-5xl font-bold ${textColor}`}>{value}</p>
      </div>
      <p className={`text-sm ${labelColor} font-medium`}>{label}</p>
    </div>
  );
}

interface ReportCardProps {
  locale: Locale;
  reportType: ReportType;
  loading: boolean;
  error: string | null;
  title: string;
  chartData: Array<Record<string, any>>;
  casesByTypeData: Array<Record<string, any>>;
  casesByCategoryData: Array<Record<string, any>>;
}

function ReportCard({
  locale,
  reportType,
  loading,
  error,
  title,
  chartData,
  casesByTypeData,
  casesByCategoryData,
}: ReportCardProps) {
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <p className="text-gray-500 text-xs lg:text-sm">
          {locale === "es" ? "Cargando reporte..." : "Loading report..."}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (reportType === "messages") {
    return (
      <MessagesReportChart title={title} chartData={chartData} locale={locale} />
    );
  }

  if (reportType === "cases_by_priority") {
    return (
      <CasesByTypeChart
        title={title}
        chartData={casesByTypeData}
        locale={locale}
      />
    );
  }

  if (reportType === "cases_by_category") {
    return (
      <CasesByCategoryChart
        title={title}
        chartData={casesByCategoryData}
        locale={locale}
      />
    );
  }

  return null;
}

interface ChartProps {
  title: string;
  chartData: Array<Record<string, any>>;
  locale: Locale;
}

function MessagesReportChart({ title, chartData, locale }: ChartProps) {
  if (chartData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
        <p className="text-gray-500 text-sm">
          {locale === "es"
            ? "No hay datos disponibles para el rango de tiempo seleccionado."
            : "No data available for the selected time range."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 12, fill: "#6b7280", fontWeight: 500 }}
            axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
            tickLine={{ stroke: "#d1d5db" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6b7280", fontWeight: 500 }}
            axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
            tickLine={{ stroke: "#d1d5db" }}
            width={50}
          />
          <Tooltip content={<CustomTooltip variant="messages" locale={locale} />} />
          <Bar
            dataKey="wapp"
            stackId="a"
            fill="#7BB662"
            name="WhatsApp"
          />
          <Bar
            dataKey="mail"
            stackId="a"
            fill="#4285F4"
            name={locale === "es" ? "Correo" : "Email"}
          />
          {chartData.some((row) => Number(row.panel) > 0) && (
            <Bar
              dataKey="panel"
              stackId="a"
              fill="#8B5CF6"
              name="Panel"
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CasesByTypeChart({ title, chartData, locale }: ChartProps) {
  if (chartData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
        <p className="text-gray-500 text-sm">
          {locale === "es"
            ? "No hay datos disponibles para el rango de tiempo seleccionado."
            : "No data available for the selected time range."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 12, fill: "#6b7280", fontWeight: 500 }}
            axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
            tickLine={{ stroke: "#d1d5db" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6b7280", fontWeight: 500 }}
            axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
            tickLine={{ stroke: "#d1d5db" }}
            width={50}
          />
          <Tooltip
            content={
              <CustomTooltip variant="cases" locale={locale} />
            }
          />
          {TYPE_SERIES.map((t) => (
            <Bar
              key={t.key}
              dataKey={t.key}
              stackId="a"
              fill={t.color}
              name={locale === "es" ? t.labelEs : t.labelEn}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CasesByCategoryChart({ title, chartData, locale }: ChartProps) {
  if (chartData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
        <p className="text-gray-500 text-sm">
          {locale === "es"
            ? "No hay datos disponibles para el rango de tiempo seleccionado."
            : "No data available for the selected time range."}
        </p>
      </div>
    );
  }

  const categoryColors = [
    "#3B82F6",
    "#8B5CF6",
    "#EC4899",
    "#F59E0B",
    "#10B981",
    "#06B6D4",
    "#EF4444",
    "#84CC16",
    "#F97316",
    "#6366F1",
    "#14B8A6",
  ];

  const categories =
    chartData.length > 0
      ? Object.keys(chartData[0]).filter((key) => key !== "name")
      : [];

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            tick={{ fontSize: 11, fill: "#6b7280", fontWeight: 500 }}
            axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
            tickLine={{ stroke: "#d1d5db" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6b7280", fontWeight: 500 }}
            axisLine={{ stroke: "#d1d5db", strokeWidth: 1 }}
            tickLine={{ stroke: "#d1d5db" }}
            width={50}
          />
          <Tooltip content={<CustomTooltip variant="cases" locale={locale} />} />
          {categories.map((category, index) => (
            <Bar
              key={category}
              dataKey={category}
              stackId="a"
              fill={categoryColors[index % categoryColors.length]}
              name={getCategoryLabel(category, locale)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomTooltip({ active, payload, variant, locale }: any) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0].payload;
  const total = Object.entries(data)
    .filter(([key]) => key !== "name" && key !== "total")
    .reduce((sum, [, value]) => sum + (value as number), 0);

  const kindWord =
    variant === "messages"
      ? locale === "es"
        ? "mensajes"
        : "messages"
      : locale === "es"
        ? "casos"
        : "cases";

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
      <p className="font-semibold text-gray-900 mb-2">{data.name}</p>
      <div className="space-y-1 text-sm">
        {payload
          .filter((item: any) => item.value && item.value > 0)
          .map((item: any, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-700">
                {item.name}: {item.value}
              </span>
            </div>
          ))}
        <div className="pt-1 border-t border-gray-200 mt-1">
          <span className="font-medium text-gray-900">Total: {total}</span>{" "}
          {kindWord}
        </div>
      </div>
    </div>
  );
}
